import * as React from 'react';
import { Component } from 'react';
import { Input } from '@makemydeal/ui-bricks/dist/cox';
import { SpinnerUI } from '@makemydeal/dr-activities-common';
import { ITradeInSearchItem } from '../../../store';
import { SearchResultsContainer, SearchInputContainer } from './VehicleInfoComponents';

export interface ISearchInputProps {
    fetchCallback: { (value: string): void };
    normalizeCallback?: { (value: string): string };
    formatResult: { (item: any, value: string): string };
    selectCallback: { (item: ITradeInSearchItem, currentValue?: string): void };
    inputId: string;
    name: string;
    label?: string;
    placeholder?: string;
    searchResults?: any[];
    isSearching?: boolean;
    errorMessage?: string;
}

export interface ISearchInputState {
    currentValue: string;
    searchResults: any[];
    focused: boolean;
    activeIndex: number;
    isSearchRunning: boolean;
}

let uniqueId = 0;

class SearchInput extends Component<ISearchInputProps, ISearchInputState> {
    searchTimeout: any;
    private inputRef = React.createRef<HTMLInputElement>();
    private selectRef = React.createRef<HTMLDivElement>();
    private wrapperRef = React.createRef<HTMLDivElement>();

    // Helper: determine if search results changed in content or order
    private haveResultsChanged = (prev: any[] = [], next: any[] = []): boolean => {
        if (prev.length !== next.length) return true; // different length
        if (prev.length === 0 && next.length === 0) return false; // both empty
        const isEqual = prev.every((obj, i) => JSON.stringify(obj) === JSON.stringify(next[i]));
        return !isEqual;
    };

    constructor(props) {
        super(props);
        this.state = {
            isSearchRunning: false,
            currentValue: '',
            searchResults: [],
            focused: false,
            activeIndex: -1
        };
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickAway);
    }

    componentDidUpdate(prevProps: ISearchInputProps) {
        // Update internal state when Redux search results change (content or order)
        const currentResults = this.props.searchResults || [];
        const prevResults = prevProps.searchResults || [];

        if (this.haveResultsChanged(prevResults, currentResults)) {
            const searchResults = currentResults.map((item) => ({
                ...item,
                _id: item._id || uniqueId++
            }));
            this.setState({ searchResults, activeIndex: -1, isSearchRunning: false });
        }

        // Reset input when error appears (no error before, but error now)
        // This handles both new errors and repeated duplicate VIN entries
        if (this.props.errorMessage && !prevProps.errorMessage) {
            this.setState({ currentValue: '', isSearchRunning: false, searchResults: [], focused: false });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickAway);
    }

    handleClickAway = (event: MouseEvent): void => {
        if (!this.wrapperRef?.current?.contains(event.target as Node)) {
            this.setState({ activeIndex: -1, focused: false });
        }
    };

    handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        switch (event.key) {
            case 'ArrowUp':
                this.handleArrowUp(event.target);
                return;
            case 'ArrowDown':
                this.handleArrowDown(event.target);
                return;
            case 'Escape':
                this.handleGoToInputSearch();
                return;
            case 'Tab':
                this.handleTab(event.shiftKey);
                return;
            case 'Enter':
                this.handleEnter();
                return;
            default:
                return;
        }
    };

    handleTab = (shiftKey: boolean): void => {
        if (shiftKey) {
            this.handleGoToInputSearch();
        } else {
            this.handleGoToNextElement();
        }
    };

    handleEnter = (): void => {
        const { searchResults, activeIndex, currentValue } = this.state;
        const { name, selectCallback } = this.props;

        if (activeIndex >= 0 && activeIndex < searchResults.length) {
            selectCallback(searchResults[activeIndex], name === 'vin' && currentValue);
            this.handleGoToNextElement();
        }
    };

    handleArrowUp = (target): void => {
        const { activeIndex, searchResults } = this.state;
        let index = activeIndex - 1;

        if (index < 0) {
            index = searchResults.length - 1;
            const lastOption = target?.parentNode?.children?.[index];
            lastOption?.focus();
        } else {
            const prevElement = target?.previousElementSibling;
            prevElement?.focus();
        }

        this.setState({ activeIndex: index });
    };

    handleArrowDown = (target): void => {
        const { activeIndex, searchResults } = this.state;
        let index = activeIndex + 1;

        if (index >= searchResults.length) {
            index = 0;
            const firstOption = target?.parentNode?.children?.[index];
            firstOption?.focus();
        } else {
            const nextElement = target?.nextElementSibling;
            nextElement?.focus();
        }

        this.setState({ activeIndex: index });
    };

    getNextFocusElement = (): HTMLElement => {
        const { name } = this.props;
        if (name === 'vin') {
            return document.querySelector<HTMLElement>('select#trim');
        } else if (name === 'ymmt') {
            return document.querySelector<HTMLElement>('select#year');
        }
        return null;
    };

    handleGoToNextElement = (): void => {
        const element = this.getNextFocusElement();
        element?.focus();
        this.setState({ activeIndex: -1, focused: false });
    };

    handleGoToInputSearch = (): void => {
        const inputSearch = this.inputRef.current;
        inputSearch?.focus();
        this.setState({ activeIndex: -1 });
    };

    async runQuery(value: string) {
        this.setState({
            isSearchRunning: true
        });

        // Dispatch Redux action - results will come through props
        this.props.fetchCallback(value);
    }

    fetchDebounced() {
        const timeoutInMs = 200;
        const fetchCallback = () => this.runQuery(this.state.currentValue);
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(fetchCallback, timeoutInMs);
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { normalizeCallback } = this.props;
        const currentValue = normalizeCallback ? normalizeCallback(event.currentTarget.value) : event.currentTarget.value;
        this.setState({ currentValue }, this.fetchDebounced);
    };

    handleSelectItem = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const { searchResults, currentValue } = this.state;
        const { name, selectCallback } = this.props;
        const selectedId = parseInt(event.currentTarget.getAttribute('data-id'), 10);
        const searchResult = searchResults.find((item) => item._id === selectedId);
        if (!searchResult) return;
        selectCallback(searchResult, name === 'vin' && currentValue);
        this.handleGoToNextElement();
    };

    handleFocusResult = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        const { currentValue, isSearchRunning } = this.state;
        if (event.shiftKey || !currentValue || isSearchRunning) return;

        if (event.key === 'Tab' || event.key === 'ArrowDown') {
            event.preventDefault();
            const firstChild = this.selectRef?.current?.querySelector<HTMLElement>('div, button');
            firstChild?.focus();
            this.setState({ activeIndex: 0 });
        }
    };

    handleFocusInput = (): void => {
        this.setState({ focused: true });
    };

    handleExitEmptyResult = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        event.preventDefault();
        if (event.key === 'Escape') {
            this.handleGoToInputSearch();
        } else if (event.key === 'Tab') {
            this.handleTab(event.shiftKey);
        }
    };

    renderResults(): JSX.Element {
        const { currentValue, searchResults, focused, activeIndex, isSearchRunning } = this.state;

        const hidden = !focused || !currentValue;

        return (
            <SearchResultsContainer ref={this.selectRef} className={`search-results list-group ${hidden ? 'hidden' : ''}`}>
                {!isSearchRunning ? null : (
                    <div className="list-group-item">
                        <SpinnerUI size="16" />
                    </div>
                )}
                {searchResults.length > 0 || isSearchRunning ? null : (
                    <div className="list-group-item" tabIndex={0} onKeyDown={this.handleExitEmptyResult}>
                        No results found
                    </div>
                )}
                {searchResults.map((item, index) => {
                    return (
                        <button
                            type="button"
                            className={`list-group-item ${activeIndex === index ? 'active' : ''}`}
                            key={item._id}
                            data-id={item._id}
                            onClick={this.handleSelectItem}
                            onKeyDown={this.handleKeyDown}
                            dangerouslySetInnerHTML={{ __html: this.props.formatResult(item, this.state.currentValue) }}
                        ></button>
                    );
                })}
            </SearchResultsContainer>
        );
    }

    render(): JSX.Element {
        const { inputId, name, label, placeholder } = this.props;
        const { currentValue } = this.state;

        return (
            <SearchInputContainer ref={this.wrapperRef} className="search-input">
                <Input
                    inputId={inputId}
                    name={name}
                    label={label}
                    value={currentValue}
                    ref={this.inputRef}
                    className="ui-bricks"
                    placeholder={placeholder}
                    onChangeFunction={this.handleChange}
                    onKeyDownFunction={this.handleFocusResult}
                    onFocusFunction={this.handleFocusInput}
                />
                <i className="fa fa-search" />
                {this.renderResults()}
            </SearchInputContainer>
        );
    }
}

export default SearchInput;
